import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import {
  RecommendationAzure,
  RecommendationAzureResponse,
} from "../../types/types";
import { RootState } from "../store";
import { createAsyncActions } from "../util";

export type EditableRecommendation = RecommendationAzure & {
  _isModified?: boolean;
  _originalValues?: Record<string, any>;
};

export type RecommendationsEditableState = {
  isLoading: boolean;
  isSaving: boolean;
  count: number | null;
  modifiedRows: Record<number, EditableRecommendation>;
  sort: {
    key: string;
    dir: string;
  };
};

export const recommendationsEditableAdapter =
  createEntityAdapter<RecommendationAzure>({
    selectId: (r) => r.recommendationId,
  });

// Async actions for loading recommendations
export const loadEditableRecommendationsActions = createAsyncActions<
  { top: number; skip: number; reset: boolean },
  void,
  {
    res: RecommendationAzureResponse;
    reset: boolean;
  }
>("recommendationsEditable/load");

// Async actions for saving recommendations
export const saveEditableRecommendationsActions = createAsyncActions<
  { recommendations: EditableRecommendation[] },
  void,
  { savedIds: number[] }
>("recommendationsEditable/save");

export const recommendationsEditableSlice = createSlice({
  name: "recommendationsEditable",
  initialState:
    recommendationsEditableAdapter.getInitialState<RecommendationsEditableState>(
      {
        isLoading: false,
        isSaving: false,
        count: null,
        modifiedRows: {},
        sort: {
          key: "recommendationCreatedAt",
          dir: "desc",
        },
      }
    ),
  reducers: {
    setModifiedRows: (
      s,
      a: PayloadAction<{
        recommendationId: number;
        data: EditableRecommendation;
      }>
    ) => {
      s.modifiedRows[a.payload.recommendationId] = a.payload.data;
    },
    removeModifiedRow: (s, a: PayloadAction<number>) => {
      delete s.modifiedRows[a.payload];
    },
    clearModifiedRows: (s) => {
      s.modifiedRows = {};
    },
    setEditableSortRecommendations: (
      s,
      a: PayloadAction<{ key: string; dir: string }>
    ) => {
      s.sort = a.payload;
    },
    updateEditableRecommendation: recommendationsEditableAdapter.updateOne,
    removeAllEditableRecommendations: recommendationsEditableAdapter.removeAll,
  },
  extraReducers: (builder) => {
    // Load actions
    builder.addCase(loadEditableRecommendationsActions.loading, (s) => {
      s.isLoading = true;
    });
    builder.addCase(loadEditableRecommendationsActions.success, (s, a) => {
      s.isLoading = false;
      const { res, reset } = a.payload;
      s.count = res["@odata.count"];
      if (reset) {
        recommendationsEditableAdapter.setAll(s, res.value);
        s.modifiedRows = {};
      } else {
        recommendationsEditableAdapter.addMany(s, res.value);
      }
    });
    builder.addCase(loadEditableRecommendationsActions.error, (s) => {
      s.isLoading = false;
    });

    // Save actions
    builder.addCase(saveEditableRecommendationsActions.loading, (s) => {
      s.isSaving = true;
    });
    builder.addCase(saveEditableRecommendationsActions.success, (s, a) => {
      s.isSaving = false;
      // Clear saved modifications and update entity store
      a.payload.savedIds.forEach((id) => {
        const modifiedRow = s.modifiedRows[id];
        if (modifiedRow) {
          recommendationsEditableAdapter.updateOne(s, {
            id,
            changes: modifiedRow,
          });
          delete s.modifiedRows[id];
        }
      });
    });
    builder.addCase(saveEditableRecommendationsActions.error, (s) => {
      s.isSaving = false;
    });
  },
});

export const recommendationsEditableReducer =
  recommendationsEditableSlice.reducer;

export const {
  setModifiedRows,
  removeModifiedRow,
  clearModifiedRows,
  setEditableSortRecommendations,
  updateEditableRecommendation,
  removeAllEditableRecommendations,
} = recommendationsEditableSlice.actions;

export const {
  selectAll: selectAllEditableRecommendations,
  selectEntities: selectEntitiesEditableRecommendations,
  selectById: selectEditableRecommendationById,
} = recommendationsEditableAdapter.getSelectors<RootState>(
  (s) => s.recommendationsEditable
);
